import type { ProductDetail } from '@repo/api-contracts';

export interface SectionKeys {
  readonly basic: string;
  readonly category: string;
  readonly color: string;
  readonly size: string;
  readonly print: string;
  readonly image: string;
  readonly price: string;
}

/**
 * 섹션 이름을 접두어로 붙인다 — 값만 직렬화하면 빈 신규 상품에서 카테고리·색상·인쇄 방식이
 * 모두 `[]` 로 같아져 React 형제 key 가 충돌하고, 리마운트 시 섹션이 복제·누락된다.
 */
const keyOf = (section: keyof SectionKeys, value: unknown): string =>
  `${section}:${JSON.stringify(value)}`;

/**
 * 에디터 섹션별 리마운트 키 — 각 섹션이 자기 서버 데이터 슬라이스에만 반응하게 한다.
 *
 * 섹션은 서버 상태를 마운트 시점에 로컬 폼으로 복사하고, 저장 후 리페치되면 key 변경으로
 * 리마운트되어 최신 서버 상태(서버 발급 id 등)를 다시 학습한다. 키를 updatedAt 하나로
 * 공유하면 어느 섹션을 저장해도 나머지 섹션이 함께 리마운트되어 입력 중이던 값이 사라진다.
 *
 * 표시 URL(presigned)은 조회마다 바뀌므로 키에서 제외하고 자산 키만 본다.
 */
export const sectionKeysOf = (detail: ProductDetail): SectionKeys => ({
  basic: keyOf('basic', [
    detail.name,
    detail.minOrderQty,
    detail.leadTimeNote,
    detail.materialNote,
    detail.measurementKind,
    detail.widthMm,
    detail.heightMm,
    detail.productionNote,
  ]),
  category: keyOf(
    'category',
    detail.categories.map((link) => [link.categoryId, link.isPrimary]),
  ),
  color: keyOf(
    'color',
    detail.colorOptions.map((option) => [
      option.id,
      option.name,
      option.colorCode,
      option.colorFamily,
      option.isAvailable,
      option.previewImage?.key ?? null,
    ]),
  ),
  size: keyOf(
    'size',
    detail.sizeOptions.map((option) => [option.id, option.name]),
  ),
  print: keyOf('print', [...detail.printMethodIds].sort()),
  image: keyOf('image', [detail.images.map((image) => image.key), detail.detailImage?.key ?? null]),
  // 가격 폼의 첫 구간은 최소 주문 수량에서 시작해야 하므로 수량 변경에도 함께 초기화한다.
  price: keyOf('price', [
    detail.minOrderQty,
    detail.priceTiers.map((tier) => [tier.id, tier.minQty, tier.unitPrice]),
  ]),
});

/**
 * 허용 폰트·잉크 색상 폼의 리마운트 키 — 상품 상세에 허용 목록이 없어 섹션이 따로 불러온 허용 id 로 만든다.
 * 정렬해 둔다. 허용 목록은 이름순이라 다른 화면에서 이름만 바꿔도 순서가 달라지는데, 그때 리마운트되면
 * 저장하지 않은 선택이 사라진다.
 */
export const allowedIdsKeyOf = (ids: readonly string[]): string => JSON.stringify([...ids].sort());
